import React from "react";
import { CircularProgress } from "@material-ui/core";
import { endOfMonth, format, startOfMonth, subDays } from "date-fns";
import api from "../../service/api";
import { validatePermission } from "../../libs/permissions";
import Card from "./Card";
import { RhythmChart, SeriesChart, TopChart } from "./Charts";
import FilterBar from "./FilterBar";
import RankingTable, { StatusDot } from "./RankingTable";
import StatTiles, { SecondaryStats } from "./StatTiles";
import { COLORS, FONT, SERIES, cardStyle, formatDateTime } from "./theme";

/**
 * Dashboard de preenchimentos.
 *
 * Uma chamada só (`/dashboard/data-forms`) traz todos os recortes; a tela
 * escolhe como desenhar. Os filtros vão juntos na mesma request, então mudar
 * qualquer um recarrega tudo de forma consistente — nunca metade da tela num
 * período e metade em outro.
 *
 * A ordem dos blocos é a ordem de leitura: o número principal, a tendência, o
 * ranking, o ritmo e por último o que ficou parado. Os tokens visuais estão em
 * `theme.js`; nenhum hex mora nos componentes.
 */

const toIso = (date) => format(date, "yyyy-MM-dd");

const PERIOD_FORMAT = { day: "2-digit", month: "2-digit", year: "numeric" };

// Cada atalho define período E granularidade: série diária de um ano não se lê,
// e série mensal de uma semana não diz nada.
const PRESET_RANGES = {
  today: () => ({ from: new Date(), to: new Date(), granularity: "day" }),
  week: () => ({ from: subDays(new Date(), 6), to: new Date(), granularity: "day" }),
  month: () => ({ from: startOfMonth(new Date()), to: endOfMonth(new Date()), granularity: "day" }),
  days30: () => ({ from: subDays(new Date(), 29), to: new Date(), granularity: "day" }),
  quarter: () => ({ from: subDays(new Date(), 89), to: new Date(), granularity: "week" }),
  year: () => ({ from: subDays(new Date(), 364), to: new Date(), granularity: "month" }),
};

class Dashboard extends React.Component {
  constructor(props) {
    super(props);

    const initial = PRESET_RANGES.days30();

    this.state = {
      loading: false,
      // Distingue "primeira carga" de "recarga": no refetch a tela anterior
      // fica visível em opacidade reduzida, em vez de piscar um spinner e
      // fazer o layout pular.
      firstLoad: true,
      error: "",
      data: null,
      fromDate: toIso(initial.from),
      toDate: toIso(initial.to),
      granularity: initial.granularity,
      formIds: [],
      activePreset: "days30",
    };
  }

  componentDidMount() {
    validatePermission();
    this.loadDashboard();
  }

  get domain() {
    return window.location.hostname.split(".")[0];
  }

  loadDashboard = () => {
    this.setState({ loading: true, error: "" });

    api
      .get("/dashboard/data-forms", {
        params: {
          domain: this.domain,
          fromDate: this.state.fromDate,
          toDate: this.state.toDate,
          granularity: this.state.granularity,
          form_ids: this.state.formIds,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      })
      .then((response) => {
        // Em erro de rede o interceptor de api.js resolve com undefined em vez
        // de rejeitar; sem esta guarda o .then quebraria ao ler .data.
        if (!response) {
          this.setState({
            loading: false,
            firstLoad: false,
            error: "Falha de comunicação com o servidor.",
          });
          return;
        }
        this.setState({ loading: false, firstLoad: false, data: response.data });
      })
      .catch(() => {
        this.setState({
          loading: false,
          firstLoad: false,
          error: "Não foi possível carregar o dashboard.",
        });
      });
  };

  // Mexer nas datas na mão desmarca o atalho: o chip aceso tem que descrever o
  // período que está na tela.
  handleChangeDate = (field, value) => {
    this.setState({ [field]: value, activePreset: null });
  };

  handleChangeGranularity = (granularity) => {
    this.setState({ granularity }, this.loadDashboard);
  };

  handleChangeForms = (formIds) => {
    this.setState({ formIds }, this.loadDashboard);
  };

  handleSelectPreset = (preset) => {
    const range = PRESET_RANGES[preset]();

    this.setState(
      {
        fromDate: toIso(range.from),
        toDate: toIso(range.to),
        granularity: range.granularity,
        activePreset: preset,
      },
      this.loadDashboard
    );
  };

  granularityLabel = () => {
    if (this.state.granularity === "week") {
      return "semana";
    }
    if (this.state.granularity === "month") {
      return "mês";
    }
    return "dia";
  };

  periodLabel = () => {
    const { period } = this.state.data;
    const from = new Date(`${period.from}T12:00:00`).toLocaleDateString("pt-BR", PERIOD_FORMAT);
    const to = new Date(`${period.to}T12:00:00`).toLocaleDateString("pt-BR", PERIOD_FORMAT);

    if (from === to) {
      return `em ${from}`;
    }
    return `de ${from} a ${to} · ${period.calendar_days} dias`;
  };

  renderHeader = () => (
    <header style={{ marginBottom: 16 }}>
      <h1
        style={{
          fontFamily: FONT,
          fontSize: 24,
          fontWeight: 600,
          letterSpacing: "-0.02em",
          color: COLORS.ink,
          margin: 0,
        }}
      >
        Dashboard de preenchimentos
      </h1>
      <p
        style={{
          fontFamily: FONT,
          fontSize: 13.5,
          color: COLORS.inkSecondary,
          margin: "5px 0 0",
        }}
      >
        {this.state.data
          ? `Como os formulários estão sendo preenchidos ${this.periodLabel()}.`
          : "Como os formulários estão sendo preenchidos."}
      </p>
    </header>
  );

  renderBody = () => {
    const { data } = this.state;
    const { kpis, series, by_form: byForm, by_user: byUser, idle_forms: idleForms } = data;

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <StatTiles kpis={kpis} periodLabel={this.periodLabel()} series={series} />

        <SeriesChart series={series} granularityLabel={this.granularityLabel()} />

        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          <TopChart
            title="Formulários mais preenchidos"
            hint="Onde o volume de registro está concentrado"
            rows={byForm}
            labelKey="form_name"
            emptyText="Nenhum preenchimento no período selecionado."
          />
          <TopChart
            title="Quem mais preencheu"
            hint="Ranking por pessoa, incluindo quem preencheu por link compartilhado"
            rows={byUser}
            labelKey="user_name"
            emptyText="Nenhum preenchimento no período selecionado."
          />
        </div>

        <RhythmChart byWeekday={data.by_weekday} byHour={data.by_hour} />

        <RankingTable
          title="Formulários sem preenchimento no período"
          hint="O que deveria ter registro e não teve — a leitura de conformidade da tela"
          rows={idleForms}
          emptyText="Todos os formulários da empresa tiveram registro no período."
          flex="1 1 100%"
          maxHeight={340}
          columns={[
            {
              title: "Formulário",
              field: "form_name",
              width: 320,
              render: (row) => (
                <StatusDot
                  color={row.last_fill_at ? COLORS.warning : COLORS.critical}
                  label={row.form_name}
                />
              ),
            },
            {
              title: "Situação",
              field: "situacao",
              render: (row) => (row.last_fill_at ? "Parado" : "Nunca preenchido"),
            },
            {
              title: "Último preenchimento",
              field: "last_fill_at",
              render: (row) => formatDateTime(row.last_fill_at),
            },
            {
              title: "Dias parado",
              field: "days_since_last_fill",
              align: "right",
              render: (row) =>
                row.days_since_last_fill === null ? "—" : row.days_since_last_fill,
            },
          ]}
        />

        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          <RankingTable
            title="Detalhe por formulário"
            hint="Os mesmos números do gráfico, exatos"
            rows={byForm}
            emptyText="Nenhum preenchimento no período selecionado."
            columns={[
              { title: "Formulário", field: "form_name", width: 240 },
              { title: "Preench.", field: "total", align: "right" },
              { title: "Pessoas", field: "users", align: "right" },
              {
                title: "Último",
                field: "last_fill_at",
                render: (row) => formatDateTime(row.last_fill_at),
              },
            ]}
          />
          <RankingTable
            title="Detalhe por pessoa"
            hint="Dias ativos separam quem preencheu muito de quem preencheu num dia só"
            rows={byUser}
            emptyText="Nenhum preenchimento no período selecionado."
            columns={[
              { title: "Pessoa", field: "user_name", width: 200 },
              { title: "Preench.", field: "total", align: "right" },
              { title: "Forms", field: "forms_count", align: "right" },
              { title: "Dias", field: "active_days", align: "right" },
              {
                title: "Último",
                field: "last_fill_at",
                render: (row) => formatDateTime(row.last_fill_at),
              },
            ]}
          />
        </div>

        <SecondaryStats kpis={kpis} />
      </div>
    );
  };

  render() {
    const { data, loading, firstLoad, error } = this.state;

    return (
      <div
        style={{
          backgroundColor: COLORS.page,
          minHeight: "100%",
          padding: "22px 22px 40px",
          boxSizing: "border-box",
        }}
      >
        {this.renderHeader()}

        <FilterBar
          fromDate={this.state.fromDate}
          toDate={this.state.toDate}
          granularity={this.state.granularity}
          formIds={this.state.formIds}
          forms={data ? data.forms : []}
          activePreset={this.state.activePreset}
          onChangeDate={this.handleChangeDate}
          onChangeGranularity={this.handleChangeGranularity}
          onChangeForms={this.handleChangeForms}
          onSelectPreset={this.handleSelectPreset}
          onApply={this.loadDashboard}
        />

        {error !== "" && (
          <Card>
            <div style={{ fontFamily: FONT, fontSize: 13.5, color: COLORS.critical }}>{error}</div>
          </Card>
        )}

        {loading && firstLoad && (
          <div style={{ ...cardStyle, padding: 60, textAlign: "center" }}>
            <CircularProgress size={28} style={{ color: SERIES.fills }} />
          </div>
        )}

        {data && error === "" && (
          // Recarga segura o desenho anterior em opacidade reduzida: sem salto
          // de layout e sem piscar esqueleto a cada troca de filtro.
          <div
            style={{
              opacity: loading ? 0.45 : 1,
              transition: "opacity 160ms ease",
              pointerEvents: loading ? "none" : "auto",
            }}
          >
            {this.renderBody()}
          </div>
        )}
      </div>
    );
  }
}

export default Dashboard;
